import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { pontoService } from '../../services/api';

const Ponto = () => {
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [lastPonto, setLastPonto] = useState(null);
  const [odometro, setOdometro] = useState('');
  const [numeroOS, setNumeroOS] = useState('');
  const [colaborador, setColaborador] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (user?.perfil === 'motorista' && user?.colaborador) {
      setColaborador(user.colaborador);
    }
  }, [user]);

  const handleRegistrarPonto = async () => {
    setLoading(true);
    try {
      if (user?.perfil === 'motorista') {
        if (!odometro || odometro.trim() === '') {
          toast.error('Odômetro é obrigatório para motoristas');
          setLoading(false);
          return;
        }
        const veiculoVinculado = colaborador?.dadosTrabalho?.veiculoVinculado;
        if (veiculoVinculado?.tipo === 'tcb') {
          if (!numeroOS || numeroOS.trim() === '') {
            toast.error('Número da OS é obrigatório para veículos TCB');
            setLoading(false);
            return;
          }
        }
      }

      let localizacao = {
        latitude: -15.8267,
        longitude: -47.9218,
        precisao: 10,
        endereco: 'Localização via GPS',
        timestamp: new Date()
      };

      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 300000
            });
          });
          localizacao = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            precisao: position.coords.accuracy,
            endereco: 'Localização obtida via GPS',
            timestamp: new Date()
          };
        } catch (geoError) {
          toast.warning('Localização não obtida, usando padrão');
        }
      }

      const dadosPonto = {
        tipo: 'batida',
        localizacao,
        deviceId: `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        observacoes: `Ponto registrado via web - ${new Date().toLocaleString('pt-BR')}`,
        ...(user?.perfil === 'motorista' && {
          odometro: parseInt(odometro),
          ...(colaborador?.dadosTrabalho?.veiculoVinculado?.tipo === 'tcb' && {
            numeroOS: numeroOS
          })
        })
      };

      const response = await pontoService.registrar(dadosPonto);

      if (response.data.success) {
        const novoPonto = {
          id: response.data.ponto._id,
          tipo: response.data.ponto.tipo,
          timestamp: response.data.ponto.data,
          colaborador: response.data.ponto.colaborador?.nome || user?.nome,
          localizacao: response.data.ponto.localizacao
        };
        setLastPonto(novoPonto);
        setOdometro('');
        setNumeroOS('');
        toast.success('Ponto registrado com sucesso! ✅');
      } else {
        throw new Error(response.data.message || 'Erro ao registrar ponto');
      }

    } catch (error) {
      console.error('Erro ao registrar ponto:', error);
      let errorMessage = 'Erro ao registrar ponto. Tente novamente.';
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        errorMessage = `Erro de validação: ${error.response.data.errors.map(err => err.msg).join(', ')}`;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const iniciais = (user?.nome || 'U').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
  const veiculo = colaborador?.dadosTrabalho?.veiculoVinculado;

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Header com gradiente */}
      <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-900 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <p className="text-indigo-300 text-sm font-medium uppercase tracking-widest mb-1">Ponto Eletrônico</p>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Bater Ponto</h1>
            <p className="text-indigo-200 mt-2 text-sm">Registre suas batidas de entrada e saída</p>
          </div>
          {/* Relógio */}
          <div className="text-center">
            <div className="text-5xl md:text-6xl font-bold tabular-nums bg-white/10 backdrop-blur px-6 py-4 rounded-2xl border border-white/20">
              {formatTime(currentTime)}
            </div>
            <p className="text-indigo-200 mt-2 text-sm capitalize">{formatDate(currentTime)}</p>
          </div>
        </div>
      </div>

      {/* Card do colaborador */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shrink-0 shadow-lg">
            {iniciais}
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-xl font-bold text-gray-900">{user?.nome || 'Não informado'}</h2>
            <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 capitalize">
                {user?.perfil || 'Perfil'}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-100">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5"></span>
                Ativo
              </span>
            </div>
          </div>
          <div className="text-center sm:text-right">
            <p className="text-xs text-gray-500">Email</p>
            <p className="text-sm font-medium text-gray-800 break-all">{user?.email || '—'}</p>
          </div>
        </div>
      </div>

      {/* Card de registro */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-50 mb-3">
            <svg className="w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Registrar Ponto</h2>
          <p className="text-gray-500 mt-1">Clique no botão para registrar sua batida</p>
        </div>

        {/* Campos para motoristas */}
        {user?.perfil === 'motorista' && (
          <div className="mb-6 space-y-4 max-w-md mx-auto">
            <div>
              <label htmlFor="odometro" className="block text-sm font-semibold text-gray-700 mb-1.5">
                Odômetro (km) *
              </label>
              <input
                type="number"
                id="odometro"
                value={odometro}
                onChange={(e) => setOdometro(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Digite a quilometragem atual"
                min="0"
                required
              />
            </div>

            {veiculo?.tipo === 'tcb' && (
              <div>
                <label htmlFor="numeroOS" className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Número da OS *
                </label>
                <input
                  type="text"
                  id="numeroOS"
                  value={numeroOS}
                  onChange={(e) => setNumeroOS(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Digite o número da Ordem de Serviço"
                  required
                />
              </div>
            )}

            {veiculo && (
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-4 flex items-center gap-3">
                <span className="text-2xl">🚛</span>
                <div>
                  <p className="text-sm font-semibold text-indigo-900">
                    {veiculo.placa} - {veiculo.modelo}
                  </p>
                  <p className="text-xs text-indigo-600 uppercase">{veiculo.tipo}</p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-center">
          <button
            onClick={handleRegistrarPonto}
            disabled={loading}
            className={`${
              loading ? 'opacity-60' : 'hover:-translate-y-0.5'
            } bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-5 px-14 rounded-2xl text-lg shadow-lg shadow-indigo-500/30 transition-all duration-200 flex items-center justify-center`}
          >
            {loading ? (
              <span className="animate-spin mr-3 inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full"></span>
            ) : (
              <svg className="w-6 h-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {loading ? 'Registrando...' : 'Registrar Ponto'}
          </button>
        </div>
      </div>

      {/* Último registro */}
      {lastPonto && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Último Registro
          </h2>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100 rounded-xl p-5">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Tipo</p>
                <p className="text-lg font-bold text-gray-900 capitalize">Batida</p>
              </div>
              <div className="hidden sm:block w-px h-12 bg-green-200"></div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Horário</p>
                <p className="text-2xl font-bold text-green-700 tabular-nums">{formatTime(lastPonto.timestamp)}</p>
              </div>
              <div className="hidden sm:block w-px h-12 bg-green-200"></div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Data</p>
                <p className="text-lg font-medium text-gray-800 capitalize">{formatDate(lastPonto.timestamp)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dicas */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-indigo-900 mb-3 flex items-center gap-2">💡 Dicas</h3>
        <ul className="space-y-2 text-indigo-800 text-sm">
          <li className="flex items-start">
            <span className="mr-2 text-indigo-400">•</span>
            Registre suas batidas sempre que necessário
          </li>
          <li className="flex items-start">
            <span className="mr-2 text-indigo-400">•</span>
            Os registros são auditados e não podem ser alterados
          </li>
          <li className="flex items-start">
            <span className="mr-2 text-indigo-400">•</span>
            Mantenha a localização ativa para um registro preciso
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Ponto;
